---
title: "Availability windows for interview scheduling"
description: "Set per-interviewer availability windows in Chosen so the AI scheduler only offers candidates times each interviewer is actually open for interviews."
category: "scheduling"
keywords:
  - "interviewer availability"
  - "scheduling availability windows"
  - "set interview hours"
  - "interviewer working hours ATS"
schema: "HowTo"
nav:
  section: "guides"
  order: 10
  parent: "/guides/scheduling"
---

Availability windows tell the AI scheduler which hours each interviewer is open for interviews. The scheduler never offers a candidate a time outside someone's window. Set them under **Settings → Scheduling → Availability**.

## What a window is

A window is the outer boundary of when you'll take interviews — say, weekdays 9am to 5pm. The default is exactly that, in your organization's timezone. You can tighten it, widen it, or blacklist specific recurring slots: Friday afternoons, the Wednesday 10–11 standup, whatever you never want booked.

Windows are per-interviewer. Each person on your team has their own, because a senior engineer who needs deep-work mornings and a recruiter who lives in their calendar do not have the same availability.

## Set your window

<Steps>
  <li>Go to **Settings → Scheduling → Availability**.</li>
  <li>Adjust the weekday hours to the range you actually take interviews in.</li>
  <li>Blacklist any recurring slots inside that range you want permanently off-limits.</li>
  <li>Save. The scheduler uses the new window on the next request.</li>
</Steps>

## The window is not your calendar

This is the part worth getting right. The window is the *envelope* — the scheduler won't even consider a time outside it. Inside the window, the scheduler still reads your live [Google Calendar](/getting-started/integrations/google-calendar) free/busy and won't book over an actual event.

So a time only gets offered to a candidate if it clears both checks: inside the window, and free on the calendar. The window handles "I'm technically free but don't interview then." The calendar handles "I have a meeting." You need both.

<Callout type="info" title="Windows do nothing without a connected calendar">
A window narrows the search, but the scheduler still needs calendar free/busy to find real openings inside it. Without [Google Calendar](/getting-started/integrations/google-calendar) connected, every minute inside the window looks free — including the ones that aren't.
</Callout>

## Every interviewer should set an honest one

The scheduler is only as good as its inputs. If four engineers run an interview loop and three leave the default 9–5, the scheduler will offer candidates times those engineers would never have agreed to — and you find out when someone declines the calendar invite.

Ask each interviewer to set a window that reflects when they will genuinely sit down for an interview. A narrower honest window beats a wide optimistic one every time.

## Limits

- Windows are recurring weekly patterns. For a one-off "I'm out next Thursday," block the time on your calendar instead — the scheduler reads that.
- The scheduler searches roughly the next five business days. A window that only opens up two weeks out won't surface within that horizon.
- Changing a window doesn't move interviews already booked. It only affects new scheduling requests.
